import { createContext, useContext } from 'react';

/**
 * Where the choice is kept between visits.
 *
 * @remarks
 * Per browser, deliberately. Which project somebody is looking at is a position in the
 * interface, not a preference about them - it should not follow them onto another machine,
 * and it must never become something the server has to be told about and re-authorize.
 */
const STORAGE_KEY = 'pc.currentProjectId';

export function readStoredProjectId(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    // A private window, or a browser configured to refuse site data. Losing the memory is a
    // smaller failure than a page that will not render.
    return '';
  }
}

export function storeProjectId(projectId: string): void {
  try {
    if (projectId) {
      window.localStorage.setItem(STORAGE_KEY, projectId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Same reasoning. The selection still works for this page; it just will not be
    // remembered after a reload.
  }
}

export interface CurrentProject {
  /** The remembered id, which may name a project the person can no longer see. */
  rememberedProjectId: string;
  remember: (projectId: string) => void;
}

/** Exported for the provider that fills it, and for nothing else. */
export const CurrentProjectContext = createContext<CurrentProject | null>(null);

/**
 * The project a page should show, and how to change it for every page at once.
 *
 * @param available
 * The projects the server returned for this person - `undefined` while they are loading.
 *
 * @remarks
 * The remembered id is honoured only when it appears in `available`. That list is the
 * permitted set as the server resolved it from GitLab membership, so a project that was
 * removed, archived, or is simply no longer granted falls back to the first one the person
 * does have rather than producing a page of failed requests.
 *
 * The stale value is left in storage rather than cleared. Clearing it would be a side effect
 * during render, and it costs nothing to keep: if the membership comes back, so does the
 * selection.
 */
export function useCurrentProject(available: readonly { id: string }[] | undefined): {
  selectedProjectId: string;
  selectProject: (projectId: string) => void;
} {
  const context = useContext(CurrentProjectContext);

  if (context === null) {
    throw new Error('useCurrentProject must be used inside a CurrentProjectProvider.');
  }

  const { rememberedProjectId, remember } = context;

  const selectedProjectId =
    available?.some((project) => project.id === rememberedProjectId) === true
      ? rememberedProjectId
      : (available?.[0]?.id ?? '');

  return { selectedProjectId, selectProject: remember };
}
